/**
 * Fiat Activation Route
 *
 * Route for fiat account activation flow.
 * This page displays the activation flow directly without a modal wrapper.
 *
 * URL: /fiat/activation/:currency (e.g., /fiat/activation/USD)
 * Supported currencies: USD, GBP, EUR, AED
 */

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '@shared-web/modules/auth/hooks/useAuth';
import { ActivationFlow } from '@shared-web/modules/fiat-flow/components/activation';
import { useFiatAccess } from '@shared-web/modules/fiat-flow/hooks/useFiatAccess';
import { FUNDS_DIRECTION } from '@shared-web/modules/fiat-flow/types';

/**
 * Supported currencies for fiat activation
 */
const SUPPORTED_CURRENCIES = ['USD', 'GBP', 'EUR', 'AED'] as const;
type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

/**
 * Check if a currency is supported
 */
function isSupportedCurrency(currency: string): currency is SupportedCurrency {
  return SUPPORTED_CURRENCIES.includes(currency as SupportedCurrency);
}

/**
 * Get payment network info for activation based on currency
 */
function useNetworkInfo(currency: SupportedCurrency) {
  const { isLogin } = useAuth();
  const { getPaymentNetworksForCurrency, currenciesData, loading } =
    useFiatAccess();

  const networks = useMemo(() => {
    return getPaymentNetworksForCurrency(currency, FUNDS_DIRECTION.DEPOSIT);
  }, [getPaymentNetworksForCurrency, currency]);

  return {
    networks,
    currenciesData,
    loading,
    isLogin,
  };
}

/**
 * Fiat Activation Page Component
 *
 * Displays the ActivationFlow component directly for fiat account activation.
 * The flow starts open and redirects/closes when completed.
 */
export default function FiatActivationRoute() {
  const { currency: currencyParam } = useParams<{ currency: string }>();
  const [open, setOpen] = useState(true);

  // Validate currency parameter
  const currency = currencyParam?.toUpperCase() ?? '';
  const isValidCurrency = isSupportedCurrency(currency);

  const { networks, currenciesData, loading, isLogin } = useNetworkInfo(
    isValidCurrency ? currency : 'USD',
  );

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  // Show error for invalid currency
  if (!isValidCurrency) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <p className="text-destructive text-h4">
            Invalid currency: {currencyParam}
          </p>
          <p className="text-muted-foreground mt-2">
            Supported currencies: {SUPPORTED_CURRENCIES.join(', ')}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-muted-foreground">Loading activation flow...</div>
      </div>
    );
  }

  // Show login required message if not authenticated
  if (!isLogin) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-muted-foreground">
          Please log in to access fiat activation.
        </div>
      </div>
    );
  }

  // Get first available network
  const firstNetwork = networks[0];

  /** TODO: uncomment after fiat activation works */
  /**
  // Show error if no payment networks are available for this currency
  if (!firstNetwork) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <p className="text-destructive text-h4">
            No payment networks available
          </p>
          <p className="text-muted-foreground mt-2">
            No payment networks are available for {currency} at this time.
          </p>
        </div>
      </div>
    );
  }
  */

  return (
    <div className="flex h-full w-full items-center justify-center">
      <ActivationFlow
        open={open}
        currency={currency}
        paymentNetwork={firstNetwork?.paymentNetwork ?? ''}
        vendorId={firstNetwork?.vendorId ?? ''}
        currencies={currenciesData}
        onOpenChange={handleOpenChange}
        isPageFlow={true}
      />
    </div>
  );
}
